import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { StoreService } from './store.service';
import { Md5 } from 'ts-md5/dist/md5';
import { of } from 'rxjs';
import Swal from 'sweetalert2';
import { isPlatformBrowser } from '@angular/common';
import { ClientPlatformService } from './client-platform.service';

@Injectable()
export class SharedService {
  today = new Date();
  startTime!: number;
  restaurantSchedule: any;
  restaurantData: any;
  restaurantDish: any;
  selected_policy: any;
  restaurant_status: any;

  private locationsData: any;

  // res_vat_rate: number;
  // res_vat_type: string;

  constructor(
    private store: StoreService,
    private clientPlatformService: ClientPlatformService,
    @Inject(PLATFORM_ID) private readonly platformId: any
  ) {
    if (this.platformId === 'browser') {
      this.store.restaurantData.subscribe(res => {
        if (Object.getOwnPropertyNames(res).length !== 0) {
          if(sessionStorage.getItem('fullSchedule')){
            const fullScheduleStr = sessionStorage.getItem('fullSchedule');
            if (!fullScheduleStr) return;
              const fullScheduleData = JSON.parse(fullScheduleStr);
              this.restaurantSchedule = fullScheduleData;
          }
          // this.restaurantSchedule = res.restaurant_data['schedule'];
          this.restaurantData = res.restaurant_data;
          // this.res_vat_rate = Number((res.restaurant_data['vat_rate'] !== null ? res.restaurant_data['vat_rate'] : 0).toFixed(2));
          // this.res_vat_type = res.restaurant_data['is_vat_included'] === true ? 'include' : 'exclude';
        }
      });
      this.store.restaurantMenu.subscribe(res => {
        if (Object.getOwnPropertyNames(res).length !== 0) {
          this.restaurantDish = res['dishes_information'];
        }
      });
      if (sessionStorage.getItem('policy')) {
        this.selected_policy = (JSON.parse(sessionStorage.getItem('policy'))).policy;
      }
    }
  }

  isPromotionAvailableOnCurrentPlatform(platform: any): boolean {
    return this.clientPlatformService.isPromotionAvailable(platform);
  }

  setLocationsData(data: any) {
    this.locationsData = data;
  }

  getLocationsData() {
    if (this.platformId === 'browser') {
      return this.locationsData ? this.locationsData : JSON.parse(sessionStorage.getItem('restaurantLocationDatas'));
    }
  }

  private resolveScheduleForTimeContext(
    schedule: any,
    currentTime: Date = new Date(),
    midnightCutoffMinutes: number = 4 * 60
  ): { schedule: any | null; baseDate: Date } {
    const baseDate = new Date(currentTime);
    if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
      return { schedule: null, baseDate };
    }

    const hasDayOfWeek = schedule.some((item: any) => item?.dayOfWeek !== undefined && item?.dayOfWeek !== null);
    if (!hasDayOfWeek) {
      return { schedule: schedule[0], baseDate };
    }

    const todayDayOfWeek = currentTime.getDay();
    const prevDayOfWeek = (todayDayOfWeek + 6) % 7;
    const todaySchedule = schedule.find((day: any) => Number(day?.dayOfWeek) === todayDayOfWeek) || null;
    const prevSchedule = schedule.find((day: any) => Number(day?.dayOfWeek) === prevDayOfWeek) || null;

    const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const isBeforeCutoff = currentMinutes < midnightCutoffMinutes;

    if (isBeforeCutoff && prevSchedule?.isMidNightShift) {
      const prevBaseDate = new Date(currentTime);
      prevBaseDate.setDate(prevBaseDate.getDate() - 1);
      return { schedule: prevSchedule, baseDate: prevBaseDate };
    }

    return { schedule: todaySchedule, baseDate };
  }

  getScheduleDisplayLabel(
    schedule: any,
    currentTime: Date = new Date(),
    midnightCutoffMinutes: number = 4 * 60
  ): string {
    const resolved = this.resolveScheduleForTimeContext(schedule, currentTime, midnightCutoffMinutes);
    const todaysSchedule = resolved.schedule;
    if (!todaysSchedule) {
      return 'Closed today';
    }

    const label = typeof todaysSchedule?.label === 'string' ? todaysSchedule.label.trim() : '';
    if (label) {
      return label;
    }

    const shifts = Array.isArray(todaysSchedule.shift) ? todaysSchedule.shift : [];
    if (shifts.length === 0) {
      return 'Closed today';
    }

    const shiftStrings = shifts
      .map((shift: any) => {
        if (!shift?.openingTime || !shift?.closingTime) return null;
        return `${this.convertTime24to12(shift.openingTime)} - ${this.convertTime24to12(shift.closingTime)}`;
      })
      .filter(Boolean);

    return shiftStrings.length > 0 ? shiftStrings.join(', ') : 'Closed today';
  }

  getLocationStatus(
    schedule: any,
    openingSoonThresholdMinutes: number = 30,
    closingSoonThresholdMinutes: number = 30,
    currentTime: Date = new Date()
  ) {
    const defaultStatus = { label: 'Closed', badgeClass: 'closing-badge', isOpenNow: false };
    const resolved = this.resolveScheduleForTimeContext(schedule, currentTime);
    const todaysSchedule = resolved.schedule;
    const baseDate = resolved.baseDate;
    if (!todaysSchedule || todaysSchedule.status === 'closed') {
      return defaultStatus;
    }

    const labelRanges = this.getLabelTimeRanges(todaysSchedule?.label, baseDate);
    if (labelRanges.length > 0) {
      const labelStatus = this.getStatusFromRanges(
        labelRanges,
        openingSoonThresholdMinutes,
        closingSoonThresholdMinutes,
        currentTime
      );
      return labelStatus || defaultStatus;
    }

    const shifts = Array.isArray(todaysSchedule.shift) ? todaysSchedule.shift : [];
    if (shifts.length === 0) {
      return defaultStatus;
    }

    for (const shift of shifts) {
      const openingTime = shift?.openingTime;
      const closingTime = shift?.closingTime;
      if (!openingTime || !closingTime) {
        continue;
      }

      const openDate = this.buildDateFromTime(openingTime, baseDate);
      let closeDate = this.buildDateFromTime(closingTime, baseDate);

      if (closeDate <= openDate) {
        closeDate.setDate(closeDate.getDate() + 1);
      }

      if (currentTime >= openDate && currentTime <= closeDate) {
        const minutesToClose = this.getMinutesDiff(currentTime, closeDate);
        if (minutesToClose <= closingSoonThresholdMinutes) {
          return { label: 'Closing soon', badgeClass: 'closing-soon-badge', isOpenNow: true };
        }
        return { label: 'Open Now', badgeClass: 'open-badge', isOpenNow: true };
      }

      if (currentTime < openDate) {
        const minutesToOpen = this.getMinutesDiff(currentTime, openDate);
        if (minutesToOpen <= openingSoonThresholdMinutes) {
          return { label: 'Opening soon', badgeClass: 'opening-soon-badge', isOpenNow: false };
        }
        break;
      }
    }

    return defaultStatus;
  }

  private getLabelTimeRanges(label: any, baseDate: Date) {
    if (!label || typeof label !== 'string') {
      return [];
    }

    const timeRangeRegex = /(\d{1,2}:\d{2}\s*(?:AM|PM)?)\s*-\s*(\d{1,2}:\d{2}\s*(?:AM|PM)?)/i;
    const ranges: { start: Date; end: Date }[] = [];

    const parts = label.split(',').map((part: string) => part.trim()).filter(Boolean);
    for (const part of parts) {
      const match = part.match(timeRangeRegex);
      if (!match) {
        continue;
      }

      const startTime = match[1].replace(/\s+/g, ' ').trim();
      const endTime = match[2].replace(/\s+/g, ' ').trim();
      const startDate = this.convertToDate(startTime, undefined, baseDate);
      const endDate = this.convertToDate(endTime, startTime, baseDate);

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        continue;
      }

      ranges.push({ start: startDate, end: endDate });
    }

    return ranges;
  }

  private getStatusFromRanges(
    ranges: { start: Date; end: Date }[],
    openingSoonThresholdMinutes: number,
    closingSoonThresholdMinutes: number,
    currentTime: Date
  ) {
    const activeRange = ranges.find(range => currentTime >= range.start && currentTime <= range.end);
    if (activeRange) {
      const minutesToClose = this.getMinutesDiff(currentTime, activeRange.end);
      if (minutesToClose <= closingSoonThresholdMinutes) {
        return { label: 'Closing soon', badgeClass: 'closing-soon-badge', isOpenNow: true };
      }
      return { label: 'Open Now', badgeClass: 'open-badge', isOpenNow: true };
    }

    const upcomingRange = ranges
      .filter(range => currentTime < range.start)
      .sort((a, b) => a.start.getTime() - b.start.getTime())[0];

    if (upcomingRange) {
      const minutesToOpen = this.getMinutesDiff(currentTime, upcomingRange.start);
      if (minutesToOpen <= openingSoonThresholdMinutes) {
        return { label: 'Opening soon', badgeClass: 'opening-soon-badge', isOpenNow: false };
      }
    }

    return null;
  }

  private buildDateFromTime(time: string, baseDate: Date) {
    const [hours, minutes] = time.split(':').map((value: string) => Number(value));
    const result = new Date(baseDate);
    result.setHours(hours || 0, minutes || 0, 0, 0);
    return result;
  }

  private getMinutesDiff(fromDate: Date, toDate: Date) {
    return (toDate.getTime() - fromDate.getTime()) / 60000;
  }


  public async newOrderingScheduleProcessing(selectedPolicy: string = this.selected_policy, scheduleData: any, processingFrom?: string): Promise<void> {
    let counter: number = 0;
    let preOrderColTimes: any = [];
    let preOrderDelTimes: any = [];
    let rawScheduleData : any;

    let collectionEnabled = false;
    let deliveryEnabled = false;
    let reservationEnabled = false;
    let firstCollectionMetaSet = false;
    let firstDeliveryMetaSet = false;


    if(processingFrom === 'from_cart') {
      // showing (saved) scheduleData when schedule processing from cart for CERTAIN INTERVAL schedule setting etc 
      // because when we calling newOrderingScheduleProcessing from cart, we don't have full schedule data we have only byDateData or byDayData (which is already modified)
      rawScheduleData = scheduleData;
    }else{
      rawScheduleData = scheduleData?.byDateData ? scheduleData?.byDateData : scheduleData.byDayData;
    }

    // console.log('RAW - SCHEDULE Data:', rawScheduleData)
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const midnightCutoffMinutes = 4 * 60; // mid night cut off time 4:00 AM 

    if (processingFrom !== 'from_cart') {
      let fullSchedule = this.restaurantSchedule;

      if (this.platformId === 'browser') {
        const fullWeekStr = sessionStorage.getItem('fullweekschedule');
        if (fullWeekStr) {
          fullSchedule = JSON.parse(fullWeekStr);
        } else if (!fullSchedule) {
          const fullScheduleStr = sessionStorage.getItem('fullSchedule');
          if (fullScheduleStr) {
            fullSchedule = JSON.parse(fullScheduleStr);
          }
        }
      }

      const scheduleDayOfWeek = rawScheduleData?.dayOfWeek ?? now.getDay();
      const prevDayOfWeek = (scheduleDayOfWeek + 6) % 7;
      const nextDayOfWeek = (scheduleDayOfWeek + 1) % 7;

      const prevDaySchedule = Array.isArray(fullSchedule)
        ? fullSchedule.find((day: any) => day?.dayOfWeek === prevDayOfWeek)
        : null;
      const nextDaySchedule = Array.isArray(fullSchedule)
        ? fullSchedule.find((day: any) => day?.dayOfWeek === nextDayOfWeek)
        : null;

      const isBeforeCutoff = currentMinutes < midnightCutoffMinutes;

      if (isBeforeCutoff && prevDaySchedule?.isMidNightShift && rawScheduleData?.shift?.length > 0) {
        const carryOverShift = rawScheduleData.shift[0];
        rawScheduleData = {
          ...prevDaySchedule,
          shift: [carryOverShift],
          isMidNightShift: false
        };
      } else if (!isBeforeCutoff && rawScheduleData?.isMidNightShift && nextDaySchedule?.shift?.length > 0 && Array.isArray(rawScheduleData?.shift)) {
        const nextDayShift = nextDaySchedule.shift[0];
        const alreadyMerged = rawScheduleData.shift.some((shift: any) => shift?._id && nextDayShift?._id && shift._id === nextDayShift._id);

        if (nextDayShift && !alreadyMerged) {
          rawScheduleData = {
            ...rawScheduleData,
            shift: [...rawScheduleData.shift, { ...nextDayShift, _isNextDayShift: true }]
          };
        }
      }
    }

    if (this.platformId === 'browser') {
      sessionStorage.setItem('rawSchedule', JSON.stringify(rawScheduleData))
    }
    if (!rawScheduleData && !this.restaurantData) {
      return;
    }
    /*
     @add custom schdeule handling code here
    */
    let myOrderingSchedule = {
      restaurantStatus: (this.restaurantData?.is_ordering === false || this.restaurantData?.restaurant_mode !== 1) ? 'CLOSED' : rawScheduleData?.isActive ? 'OPEN' : 'CLOSED',
      orderingStatus: 'CLOSED',
      isDelivery: this.restaurantData?.is_delivery_closed_for_today ? false : this.restaurantData.is_delivery,
      isCollection: this.restaurantData?.is_collection_closed_for_today ? false : this.restaurantData.is_collection,
      isOrdering: this.restaurantData?.is_ordering,
      isReservation: this.restaurantData?.is_reservation,
      opening_hours: '',
      collection_startTime: '',
      delivery_startTime: '',
      preOrderCollecitonTimes: [],
      preOrderDeliveryTimes: [],
      orderingMode: { collection: 'NONE', delivery: 'NONE' }, // asap, pre, none
      openShiftIndex: null
    }

    let isCollectionPreOrderChangeable = true;

    if (this.restaurantData && myOrderingSchedule?.isOrdering && rawScheduleData?.isActive && rawScheduleData.shift.length > 0) {
      rawScheduleData.shift.forEach((scheduleItem: any, index: number) => {
        counter = counter + 1;
        if (scheduleItem) {
          const shiftOffsetMinutes = scheduleItem?._isNextDayShift ? 24 * 60 : 0;
          const openingMinutes = this.getMinutes(scheduleItem.openingTime) + shiftOffsetMinutes;
          const closingMinutes = this.getMinutes(scheduleItem.closingTime) + shiftOffsetMinutes;
          const collectionStartMinutes = this.getMinutes(scheduleItem.collection.startTime) + shiftOffsetMinutes;
          const collectionEndMinutes = this.getMinutes(scheduleItem.collection.endTime) + shiftOffsetMinutes;
          const deliveryStartMinutes = this.getMinutes(scheduleItem.delivery.startTime) + shiftOffsetMinutes;
          const deliveryEndMinutes = this.getMinutes(scheduleItem.delivery.endTime) + shiftOffsetMinutes;
          const reservationStartMinutes = scheduleItem.reservation?.startTime
            ? this.getMinutes(scheduleItem.reservation.startTime) + shiftOffsetMinutes
            : null;
          const reservationEndMinutes = scheduleItem.reservation?.endTime
            ? this.getMinutes(scheduleItem.reservation.endTime) + shiftOffsetMinutes
            : null;

          myOrderingSchedule.opening_hours = myOrderingSchedule.opening_hours.concat(` (${this.convertTime24to12(scheduleItem.openingTime)} - ${this.convertTime24to12(scheduleItem.closingTime)}) `);
          if (currentMinutes >= openingMinutes && currentMinutes <= closingMinutes) {
            // OPEN
            myOrderingSchedule.openShiftIndex = index;
            myOrderingSchedule.orderingStatus = 'ASAP';
            myOrderingSchedule.restaurantStatus = 'OPEN';
            myOrderingSchedule.isCollection = false;
            myOrderingSchedule.isDelivery = false;

            
            if (this.restaurantData.is_collection) {
              if (!this.restaurantData?.is_collection_closed_for_today && currentMinutes >= collectionStartMinutes && currentMinutes <= collectionEndMinutes && scheduleItem.collection.isActive) {
                myOrderingSchedule.isCollection = true;
                myOrderingSchedule.orderingMode.collection = 'ASAP';
                let collectionTime = scheduleItem.collection.collectionTime;
                isCollectionPreOrderChangeable = false;
                /*OVERRIDE TIME WITH EXTEND collection TIME*/
                if (this.restaurantData?.is_collection_time_extended) {
                  collectionTime = this.restaurantData?.default_collection_time
                }


                preOrderColTimes = [...preOrderColTimes, ...scheduleItem.collection.timeList]; // merge times
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { collectionTime: collectionTime, minCollectionAmount: scheduleItem.collection.minCollectionAmount, preOrderCollecitonTimes: scheduleItem.collection.timeList });
              } else if (!this.restaurantData?.is_collection_closed_for_today && currentMinutes < collectionStartMinutes && currentMinutes < collectionEndMinutes && scheduleItem.collection.isActive) {
                myOrderingSchedule.isCollection = true;
                myOrderingSchedule.orderingMode.collection = 'PRE-ORDER';
                let collectionTime = scheduleItem.collection.collectionTime;
                /*OVERRIDE TIME WITH EXTEND collection TIME*/
                if (this.restaurantData?.is_collection_time_extended) {
                  collectionTime = this.restaurantData?.default_collection_time
                }

                preOrderColTimes = [...preOrderColTimes, ...scheduleItem.collection.timeList]; // merge times
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { collectionTime: collectionTime, minCollectionAmount: scheduleItem.collection.minCollectionAmount, preOrderCollecitonTimes: scheduleItem.collection.timeList });


              } 
            } else {
              //COLLECTION IS DISABLED FROM RESTAURANT SETTING
              myOrderingSchedule.orderingMode.collection = 'NONE';
              myOrderingSchedule.isCollection = false;
            }

            if (this.restaurantData.is_delivery) {
              if (!this.restaurantData?.is_delivery_closed_for_today && currentMinutes >= deliveryStartMinutes && currentMinutes <= deliveryEndMinutes && scheduleItem.delivery.isActive) {
                myOrderingSchedule.isDelivery = true;
                myOrderingSchedule.orderingMode.delivery = 'ASAP';
                let deliveryTime = scheduleItem.delivery.deliveryTime;
                /*OVERRIDE TIME WITH EXTEND delivery TIME*/
                if (this.restaurantData?.is_delivery_time_extended) {
                  deliveryTime = this.restaurantData?.default_delivery_time
                }

                preOrderDelTimes = [...preOrderDelTimes, ...scheduleItem.delivery.timeList];
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { deliveryTime: deliveryTime, minDeliveryAmount: scheduleItem.delivery.minDeliveryAmount, preOrderDeliveryTimes: scheduleItem.delivery.timeList });
              }
              else if (!this.restaurantData?.is_delivery_closed_for_today && currentMinutes < deliveryStartMinutes && currentMinutes < deliveryEndMinutes && scheduleItem.delivery.isActive) {
                myOrderingSchedule.isDelivery = true;
                myOrderingSchedule.orderingMode.delivery = 'PRE-ORDER';
                let deliveryTime = scheduleItem.delivery.deliveryTime;
                /*OVERRIDE TIME WITH EXTEND delivery TIME*/
                if (this.restaurantData?.is_delivery_time_extended) {
                  deliveryTime = this.restaurantData?.default_delivery_time
                }

                preOrderDelTimes = [...preOrderDelTimes, ...scheduleItem.delivery.timeList];
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { deliveryTime: deliveryTime, minDeliveryAmount: scheduleItem.delivery.minDeliveryAmount, preOrderDeliveryTimes: scheduleItem.delivery.timeList });
              }
            }else {
              myOrderingSchedule.orderingMode.delivery = 'NONE';
              myOrderingSchedule.isDelivery = false;
            }

            if (this.restaurantData.is_reservation) {
              if (reservationStartMinutes !== null && reservationEndMinutes !== null && currentMinutes >= reservationStartMinutes && currentMinutes <= reservationEndMinutes && scheduleItem.reservation.isActive) {
                myOrderingSchedule.isReservation = true;
              }
              else if (reservationStartMinutes !== null && reservationEndMinutes !== null && currentMinutes < reservationStartMinutes && currentMinutes < reservationEndMinutes && scheduleItem.reservation.isActive) {
                myOrderingSchedule.isReservation = true;
              }
              else {
                myOrderingSchedule.isReservation = false;
              }
            } else {
              myOrderingSchedule.isReservation = false;
            }
          } else {
            if(this.restaurantData?.is_collection){
              if(!this.restaurantData?.is_collection_closed_for_today && (scheduleItem?.collection?.isActive && currentMinutes < collectionStartMinutes)){
                preOrderColTimes = [...preOrderColTimes, ...scheduleItem.collection.timeList];
                myOrderingSchedule.isCollection = true;
                myOrderingSchedule.orderingMode.collection = myOrderingSchedule.orderingMode.collection === 'NONE' ? 'PRE-ORDER' : myOrderingSchedule.orderingMode.collection;
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { collectionTime: this.restaurantData?.is_collection_time_extended ? this.restaurantData?.default_collection_time : scheduleItem.collection.collectionTime, minCollectionAmount: scheduleItem.collection.minCollectionAmount });
              }
            }
            if(this.restaurantData?.is_delivery){
              if(!this.restaurantData?.is_delivery_closed_for_today && (scheduleItem?.delivery?.isActive && currentMinutes < deliveryStartMinutes)){
                preOrderDelTimes = [...preOrderDelTimes, ...scheduleItem.delivery.timeList];
                myOrderingSchedule.isDelivery = true;
                myOrderingSchedule.orderingMode.delivery = myOrderingSchedule.orderingMode.delivery === 'NONE' ? 'PRE-ORDER' : myOrderingSchedule.orderingMode.delivery;
                myOrderingSchedule = Object.assign({}, myOrderingSchedule, { deliveryTime: this.restaurantData?.is_delivery_time_extended ? this.restaurantData?.default_delivery_time : scheduleItem.delivery.deliveryTime, minDeliveryAmount: scheduleItem.delivery.minDeliveryAmount });
              }
            }
            myOrderingSchedule.restaurantStatus = myOrderingSchedule.orderingStatus === 'ASAP' ? 'OPEN' : 'PRE-ORDER';
            myOrderingSchedule.orderingStatus = myOrderingSchedule.orderingStatus === 'ASAP' ? myOrderingSchedule.orderingStatus : 'PRE-ORDER';

            if (myOrderingSchedule?.restaurantStatus !== 'OPEN') {
              if (currentMinutes < openingMinutes) {

                // Save the first upcoming shift index only once
                if (myOrderingSchedule.openShiftIndex === null) {
                  myOrderingSchedule.openShiftIndex = index;
                  // collection start time is always the first upcoming shift's collection start
                  myOrderingSchedule.collection_startTime = scheduleItem.collection.startTime;

                  // delivery start time -> first upcoming shift with delivery active
                  let deliveryStart = null;
                  for (let d = index; d < rawScheduleData.shift.length; d++) {
                    if (rawScheduleData.shift[d].delivery?.isActive) {
                      deliveryStart = rawScheduleData.shift[d].delivery.startTime;
                      break;
                    }
                  }
                  myOrderingSchedule.delivery_startTime = deliveryStart;
                }

                myOrderingSchedule.restaurantStatus =
                  myOrderingSchedule.orderingStatus === 'ASAP' ? 'OPEN' : 'PRE-ORDER';

                myOrderingSchedule.orderingStatus =
                  myOrderingSchedule.orderingStatus === 'ASAP'
                    ? myOrderingSchedule.orderingStatus
                    : 'PRE-ORDER';

                // ------------ COLLECTION: accumulate, do NOT overwrite false -> true ------------
                if (this.restaurantData.is_collection) {
                  // Pre-order collection is valid only if collection isn't closed for today and current < shift collection start
                  if (!this.restaurantData?.is_collection_closed_for_today && currentMinutes < collectionStartMinutes && scheduleItem.collection.isActive) {
                    // Mark collection available
                    collectionEnabled = true;
                    myOrderingSchedule.isCollection = true;

                    // Set collection meta fields only once (first active future collection shift)
                    if (!firstCollectionMetaSet) {
                      myOrderingSchedule = Object.assign({}, myOrderingSchedule, {
                        collectionTime: this.restaurantData?.is_collection_time_extended
                          ? this.restaurantData?.default_collection_time
                          : scheduleItem.collection.collectionTime,
                        minCollectionAmount: scheduleItem.collection.minCollectionAmount,
                      });
                      firstCollectionMetaSet = true;
                    }

                    // Merge future collection time list
                    preOrderColTimes = [...preOrderColTimes, ...scheduleItem.collection.timeList];
                  } else {
                    // don't set orderingMode.collection = 'NONE' here — wait until after loop
                  }
                }

                // ------------ DELIVERY: accumulate, do NOT overwrite false -> true ------------
                if (this.restaurantData.is_delivery) {
                  // For delivery pre-order: if delivery is active for this future shift, collect it
                  if (!this.restaurantData?.is_delivery_closed_for_today && scheduleItem.delivery?.isActive) {
                    deliveryEnabled = true;
                    myOrderingSchedule.isDelivery = true;

                    // Set delivery meta fields only once (first active future delivery shift)
                    if (!firstDeliveryMetaSet) {
                      myOrderingSchedule = Object.assign({}, myOrderingSchedule, {
                        deliveryTime: this.restaurantData?.is_delivery_time_extended
                          ? this.restaurantData?.default_delivery_time
                          : scheduleItem.delivery.deliveryTime,
                        minDeliveryAmount: scheduleItem.delivery.minDeliveryAmount,
                      });
                      firstDeliveryMetaSet = true;
                    }

                    // Merge delivery times
                    preOrderDelTimes = [...preOrderDelTimes, ...scheduleItem.delivery.timeList];
                  } else {
                    // If this shift's delivery is inactive, do not overwrite previously set deliveryEnabled
                    // but still allow future shifts to add to preOrderDelTimes (handled above when active)
                  }
                }

                // ------------ RESERVATION: accumulate similarly ------------
                if (this.restaurantData.is_reservation) {
                  if (reservationStartMinutes !== null && currentMinutes < reservationStartMinutes && scheduleItem.reservation?.isActive) {
                    reservationEnabled = true;
                  }
                }
              } else {
                if (rawScheduleData.shift.length === counter) {
                  myOrderingSchedule.isOrdering = false;
                  myOrderingSchedule.restaurantStatus = 'CLOSED'
                }
                myOrderingSchedule.isCollection = false;
                myOrderingSchedule.isDelivery = false;
                myOrderingSchedule.isReservation = false;
              }
            }
          }

          const uniqueTimesPreOrderColTimes = [...new Set(preOrderColTimes)];
          const uniqueTimesPreOrderDeliveryTimes = [...new Set(preOrderDelTimes)];
          myOrderingSchedule.preOrderCollecitonTimes = uniqueTimesPreOrderColTimes;
          myOrderingSchedule.preOrderDeliveryTimes = uniqueTimesPreOrderDeliveryTimes;
        }
      });
      
    }
    if (rawScheduleData?.status === 'closed') {
      myOrderingSchedule.restaurantStatus = 'CLOSED';
      myOrderingSchedule.isReservation = false;
      myOrderingSchedule.isDelivery = false;
      myOrderingSchedule.isOrdering = false;
      myOrderingSchedule.isCollection = false;
    }

    if (this.platformId === 'browser') {
      sessionStorage.setItem('todaysSchedule', JSON.stringify(myOrderingSchedule))
    }
    this.store.saveSchedule(myOrderingSchedule); //redux store
  }
  getCurrentShiftDetails(shiftList: any) {
    // Get current time in "HH:MM" format
    const now = new Date();
    const currentTime = `${this.pad(now.getHours())}:${this.pad(now.getMinutes())}`;

    // Find the shift based on current time
    const currentShift = shiftList.find((shift: any) => {
      return this.isTimeWithinRange(currentTime, shift.openingTime, shift.closingTime);
    });

    if (currentShift) {
      return {
        shiftName: currentShift.shiftName,
        timeRangeType: currentShift.delivery.timeRangeType,
        timeSlotGap: currentShift.delivery.timeSlotGap,
        timeRangeGap: currentShift.delivery.timeRangeGap
      };
    } else {
      return {
        shiftName: '',
        timeRangeType: '',
        timeSlotGap: '',
        timeRangeGap: ''
      };
    }
  }

  pad(num: number): string {
    return num.toString().padStart(2, '0');
  }

  // footer schedule data 
  formattedFullSchedule(data: any) {
    const formattedData = data.map((day: any) => {
      // Filter only active reservations
      const activeShifts = day?.shift.filter((shift: any) => shift?.reservation?.isActive);
  
      // Determine if any reservation is active
      const isReservationActive = day.shift.some((shift: any) => shift?.reservation?.isActive);
  
      return {
        dayOfWeek: day?.dayOfWeek,
        dayName: day?.dayName,
        status: day?.status,
        scheduleType: day?.scheduleType,
        label: day?.label,
        isActive: day?.isActive,
        isReservationActive: isReservationActive,
        shift: activeShifts.map((shift: any) => ({
          shiftName: shift?.shiftName,
          isActive: shift?.reservation?.isActive,
          openingTime: this.convertTime24to12(shift?.openingTime),
          closingTime: this.convertTime24to12(shift?.closingTime),
          timeList: shift?.reservation?.timeList
        }))
      };
    });
  
    return formattedData;
  }

  isTimeWithinRange(currentTime: string, openingTime: string, closingTime: string): boolean {
    return currentTime >= openingTime && currentTime <= closingTime;
  }
  validateUserSelectedTime(): void {
    if (this.platformId === 'browser') {
      if (sessionStorage.getItem('policy')) {
        let currentTime = new Date();
        const currentDate = new Date();
        let userSelectedTimeObject = JSON.parse(sessionStorage.getItem('policy'));
        this.selected_policy = userSelectedTimeObject;
        // this.scheduleProcessing(this.selected_policy.policy);
        const timeIn24 = this.convertTime12to24(userSelectedTimeObject.when);
        let [hour, min] = timeIn24.split(":");
        let userTimeDate = new Date().setHours(Number(hour), Number(min), 0);
        /**
         * IF TIME ALREADY PASSED AWAY
         * AND USER BACK TO PLACE ORDER
        */
        if (currentTime >= new Date(userTimeDate)) {
          /**
           * As time is gone, re-generate schedule
           * **/

          if (userSelectedTimeObject.status === 'ASAP') {
            currentDate.setMinutes(new Date().getMinutes() + Number(userSelectedTimeObject.delivery_time));
            const minutes = currentDate.getMinutes();
            let roundedMinutes = Math.ceil(minutes / 5) * 5;
            const roundDelta = roundedMinutes - minutes;
            if (roundDelta > 0 && roundDelta < 3) {
              roundedMinutes += 5;
            }
            currentDate.setMinutes(roundedMinutes);
            //const time = currentDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
            const time = this.convertTime24to12(currentDate.toTimeString().substring(0, 5));
            userSelectedTimeObject.when = time;
            this.store.saveSelectedPolicy(userSelectedTimeObject);
            sessionStorage.setItem('policy', JSON.stringify(userSelectedTimeObject));
          }

        } else {
          /**
           * - USER IN TIME
           * - FIND HOW LONG HAVE PASSED 
           * - IF PASSWED MAX NUM OF TIME
           * - check schdeule policy time in num 
           * - RE-ASSIGN ASAP TIME AUTOMATICALLY
           * 
          */

          let difference = new Date(userTimeDate).getTime() - currentTime.getTime(); // This will give difference in milliseconds
          let resultInMinutes = Math.round(difference / 60000);
          let policyData = JSON.parse(sessionStorage.getItem('policy'));
          if (resultInMinutes < policyData.delivery_time) {
            if (userSelectedTimeObject.status === 'ASAP') {
              currentDate.setMinutes(new Date().getMinutes() + Number(userSelectedTimeObject.delivery_time));
              const minutes = currentDate.getMinutes();
              let roundedMinutes = Math.ceil(minutes / 5) * 5;
              const roundDelta = roundedMinutes - minutes;
              if (roundDelta > 0 && roundDelta < 3) {
                roundedMinutes += 5;
              }
              currentDate.setMinutes(roundedMinutes);
              //const time = currentDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
              const time = this.convertTime24to12(currentDate.toTimeString().substring(0, 5))
              userSelectedTimeObject.when = time;
              this.store.saveSelectedPolicy(userSelectedTimeObject);
              sessionStorage.setItem('policy', JSON.stringify(userSelectedTimeObject));
            }
          }

        }
      }
    }
  }

  setDeliveryTime(timeObject: any): void {
    let time = {
      status: timeObject.status === 'OPEN' ? 'ASAP' : 'PRE-ORDER',
      when: timeObject.asap ? timeObject.asap : timeObject.today[0].time,
      res_status: timeObject.status
    }
    this.store.storeDeliveryTime(time);
    if (this.platformId === 'browser') {
      sessionStorage.setItem('time', JSON.stringify(time));
    }
  }

  convertTime24to12 = (time24: any) => {
    const [sHours, minutes] = time24.match(/([0-9]{1,2}):([0-9]{2})/).slice(1);
    const period = +sHours < 12 ? 'AM' : 'PM';
    const hours = +sHours % 12 || 12;

    return `${hours}:${minutes} ${period}`;
  }
  convertTime12to24 = (time12: any) => {
    const [sHours, minutes, period] = time12.match(/([0-9]{1,2}):([0-9]{2}) (AM|PM|am|pm)/).slice(1);
    const PM = period === 'PM';
    const hours = (+sHours % 12) + (PM ? 12 : 0);

    return `${('0' + hours).slice(-2)}:${minutes}`;
  }

  getMinutes(time24h: any) {
    var time = time24h.split(':');
    return time[0] * 60 + time[1] * 1;
  }

  getMinutesNow() {
    var timeNow = new Date();
    let time = timeNow.getHours() * 60 + timeNow.getMinutes();
    return time;
  }

  isToday(date: any) {
    const today = new Date();
    if (today.toDateString() === date.toDateString()) {
      return true;
    }
    return false;
  }

  hoursToMinutes(hours: number) {
    if (hours) {
      return (hours * 60);
    }
    return 0;
  }

  public BookingScheduleProcessing(date: string, advanceHours?: number, timeGap?: number, fullSchedule?: any) {
    if(fullSchedule){
      this.restaurantSchedule = fullSchedule ? fullSchedule : this.restaurantSchedule;
    }
    let selectedDate = new Date(date)
    return new Promise((resolve, reject) => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = days[new Date(selectedDate).getDay()];
      if ((this.restaurantSchedule && this.restaurantSchedule.length > 0) && this.restaurantData['is_reservation']) {
        const schedule = this.restaurantSchedule.find((f: any) => f.dayName === dayName);
        if (schedule?.status === 'closed') {
          let _msg = "<strong>The restaurant is closed for selected date.</strong> <br>Please contact with restaurant if you have any inquiry";
          resolve({ status: 'CLOSED', msg: _msg });
          return false;
        }
        if (schedule?.isReservationActive === false) {
          let _msg = "<strong>The restaurant is closed for selected date.</strong> <br>Please contact with restaurant if you have any inquiry";
          resolve({ status: 'CLOSED', msg: _msg });
          return false;
        }
        if (!schedule) {
          let _msg = "<strong>it\'s a holiday, You can select another date</strong> <br>Please contact with restaurant if you have any inquiry";
          resolve({ status: 'CLOSED', msg: _msg });
          return false;
        }
        let now: number = this.isToday(selectedDate) ? (this.getMinutesNow() + this.hoursToMinutes(advanceHours)) : 0;
        if (now >= 1440) {
          let _msg = advanceHours ? `You have to book before ${advanceHours} hours ago.` : 'We are fully book today.';
          resolve({ status: 'CLOSED', msg: _msg });
          return false;
        }
        let timesArray = [];
        let filteredTimes : any;
        schedule['shift'].forEach((schedule: any, index: any) => {
          if (!schedule.timeList) return false;

          let opening_time24 = this.convertTime12to24(schedule['openingTime']);
          let closing_time24 = this.convertTime12to24(schedule['closingTime']);

          let start = this.getMinutes(opening_time24);
          let end = this.getMinutes(closing_time24);
          if (now < end) {
            timesArray = [...timesArray, ...schedule.timeList];
          }
        });

        let min = this.hoursToMinutes(advanceHours);
        let currentDate = new Date(new Date().setMinutes(new Date().getMinutes() + min));

        filteredTimes = timesArray.filter((item: any) => {
        const timeDate = this.convertToDate(item?.time);
        return timeDate > currentDate;
        });

        const formatSelectedDate = new Date(date).setHours(0, 0, 0, 0)
        const formatTodayDate = new Date().setHours(0, 0, 0, 0)

        if(formatSelectedDate === formatTodayDate){
          resolve(filteredTimes);
        }else{
          resolve(timesArray);
        }

      } else {
        reject(null);
      }
    });
  }


  private generateTimeList(opening: any, closing: any, gapTime: number = 15) {
    const start = new Date(opening);
    const end = new Date(closing);
    let minutesArray: any[] = [];
    for (let i = 0; i < 60; i++) {
      if ((gapTime * i) > 60) break;
      minutesArray.push(gapTime * i);
    }

    const newMinutes = minutesArray.find(item => item >= start.getMinutes());
    start.setMinutes(newMinutes);

    var arr = [];
    while (start <= end) {
      let time = this.convertTime24to12(start.toTimeString().substring(0, 5))
      arr.push(time);
      start.setMinutes(start.getMinutes() + gapTime);
    }
    return arr;
  }

  public changeTimeFormat(date: any) {
    // IT CONVERTS TO 24 HOUR TIME FORMATE TO 12 HOUR TIME FORMAT
    const h = date.getHours();
    let m = date.getMinutes();
    m = ('0' + m).slice(-2);
    let _time = '';
    if (h >= 12) {
      _time = (h > 12) ? ('0' + (h - 12)).slice(-2) + ':' + m + ' ' + 'PM' : ('0' + h).slice(-2) + ':' + m + ' ' + 'PM';
    } else {
      _time = ('0' + h).slice(-2) + ':' + m + ' ' + 'AM';
    }
    return _time;
  }

  checkPromotionEligibility(totalAmount: any, discountsData: any, offersData: any) {
    /*  Default = 1
     Only Collection = 2
     Only Delivery = 3
     Both (Collection & Delivery) = 4
     First Order = 5
     Cash Only = 6
     Card = 7
     others = 8*/

    return new Promise(resolve => {
      const currentTime = this.today;
      let offers = [];
      let discount = [];
      if (this.platformId === 'browser') {
        let selected_policy: any;
        let user: any;
        let selected_payment: any;
        let convertedWhenTime : any;
        let res_status: any;
        /*@Override@*/
        this.store.selected_policy.subscribe((result: any) => {
          if (result && result['policy']) {
            convertedWhenTime = this.convertToDate(result?.when);
            selected_policy = result['policy'] === 'COLLECTION' ? 1 : 2;
            res_status = result?.res_status;
          } else {
            selected_policy = 2;
          }
        });
        this.store.user.subscribe((result: any) => {
          if (result && result['isLogged'] === true) {
            user = result['data'];
          }
        });
        this.store.paymentMethod.subscribe((result: any) => {
          if (result && result['is_active']) {
            selected_payment = result['paymentType'] === 'CARD' ? 2 : 1
          }
        });
        setTimeout(() => {
          if (offersData.length > 0) {
            /**
             * COLLECTION AND DELIVERY BOTH= 0
             * COLLECTION = 1
             * DELIVERY = 2
             * **/
            const offData = offersData.filter((item: any) =>
              item?.its_type === 2
              && totalAmount >= item.offer_eligible_amount
              && item.is_active === true
              && this.clientPlatformService.isPromotionAvailable(item.platform)
            );

            if (offData.length > 0) {
              offData.forEach((item: any) => {
                if (Number(item.offer_eligible_for) === selected_policy || Number(item.offer_eligible_for) === 0) {
                  /**
                   * - CHECK ELIGIBLE PAYMENT METHODS CASH = 1, CARD = 2, BOTH = 0
                   * - CHECK DISCOUNT START DATE AND END DATE IF NO DATE FIXED THEN IT'S FOR ALL DAY
                   * - CHECK DISCOUNT AVAILABLE TIMES
                   * - CHECK DEFAULT (AUTOMATIC ACCEPT DISCOUNT)
                   * - CHECK IS IT FIRST ORDER BY CUSTOMER
                   * **/

                  if (selected_payment) {
                    if (item.eligible_payment_methods === selected_payment || item.eligible_payment_methods === 0) {
                      /*
                        * - EITHER CASH OR CARD * CASH = 1; CARD = 2
                        * */

                      if (item.start_date !== null && item.end_date !== null) {
                        /**
                         * - IF START AND END DATE ASSIGNED
                         */
                        const start_date = new Date(item.start_date);
                        const end_date = new Date(item.end_date);
                        if (currentTime >= start_date && currentTime <= end_date) {
                          if (item.offer_available_time.length > 0) {
                            const dayName = currentTime.getDay();
                            const _times = item.offer_available_time.filter(book => book.day_name === dayName);
                            if (_times.length > 0) {
                              const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                              const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                              // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                              if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                                if (item.is_default) {
                                  /*** - COUNT USER PREVIOUS ORDERS * **/
                                  if (item.is_first_order && (user && user.total_orders === 0)) {
                                    offers.push(item);
                                  }

                                  if (item.is_first_order === false) {
                                    offers.push(item);
                                  }
                                  // default discount added directly
                                  if (!sessionStorage.getItem('promoId')) {
                                    this.store.saveAcceptedPromotion(item);
                                  }
                                } else {
                                  /**
                                   * - NOT DEFAULT
                                   * - USER HAVE TO ACCEPT DISCOUNT
                                   * **/

                                  if (item.is_first_order && (user && user.total_orders === 0)) {
                                    offers.push(item);
                                  }

                                  if (item.is_first_order === false) {
                                    offers.push(item);
                                  }
                                }
                              }
                            } // end time compare
                          } else {
                            /**
                             * - NOT TIME GIVEN
                             * **/
                            if (item.is_default) {
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                offers.push(item);
                              }

                              if (item.is_first_order === false) {
                                offers.push(item);
                              }
                              // default discount added directly
                              if (!sessionStorage.getItem('promoId')) {
                                this.store.saveAcceptedPromotion(item);
                              }
                            } else {
                              /**
                               * - NOT DEFAULT * - USER HAVE TO ACCEPT DISCOUNT
                               * **/

                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                offers.push(item);
                              }

                              if (item.is_first_order === false) {
                                offers.push(item);
                              }
                            }
                          }
                        }
                      } else {
                        /*
                          * - IF START AND END DATE NOT GIVEN.
                          * */
                        if (item.offer_available_time.length > 0) {
                          const dayName = currentTime.getDay();
                          const _times = item.offer_available_time.filter(book => book.day_name === dayName);
                          if (_times.length > 0) {
                            const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                            // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                            if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                              if (item.is_default) {
                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  offers.push(item);
                                }

                                if (item.is_first_order === false) {
                                  offers.push(item);
                                }
                                // default discount added directly
                                if (!sessionStorage.getItem('promoId')) {
                                  this.store.saveAcceptedPromotion(item);
                                }
                              } else {
                                /**
                                 * - NOT DEFAULT
                                 * - USER HAVE TO ACCEPT DISCOUNT
                                 * **/

                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  offers.push(item);
                                }

                                if (item.is_first_order === false) {
                                  offers.push(item);
                                }
                              }
                            }
                          } // end time compare
                        } else {
                          if (item.is_default) {
                            /**
                             * - COUNT USER PREVIOUS ORDERS
                             * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              offers.push(item);
                            }

                            if (item.is_first_order === false) {
                              offers.push(item);
                            }
                            // default discount added directly
                            if (!sessionStorage.getItem('promoId')) {
                              this.store.saveAcceptedPromotion(item);
                            }
                          } else {
                            /**
                             * - NOT DEFAULT
                             * - USER HAVE TO ACCEPT DISCOUNT
                             * **/

                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              offers.push(item);
                            }

                            if (item.is_first_order === false) {
                              offers.push(item);
                            }
                          }
                        }
                      }
                    }
                  } else {
                    /**
                     * - IF USER DID NOT SELECT PAYMENT METHOD YET!
                     * **/

                    /*
                      * - EITHER CASH OR CARD * CASH = 1; CARD = 2
                      * */

                    if (item.start_date !== null && item.end_date !== null) {
                      /**
                       * - IF START AND END DATE ASSIGNED
                       */
                      const start_date = new Date(item.start_date);
                      const end_date = new Date(item.end_date);
                      if (currentTime >= start_date && currentTime <= end_date) {
                        if (item.offer_available_time.length > 0) {
                          const dayName = currentTime.getDay();
                          const _times = item.offer_available_time.filter((book: any) => book.day_name === dayName);
                          if (_times.length > 0) {
                            const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                            // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                            if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                              if (item.is_default) {
                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  offers.push(item);
                                }

                                if (item.is_first_order === false) {
                                  offers.push(item);
                                }
                                // default discount added directly
                                if (!sessionStorage.getItem('promoId')) {
                                  this.store.saveAcceptedPromotion(item);
                                }
                              } else {
                                /**
                                 * - NOT DEFAULT
                                 * - USER HAVE TO ACCEPT DISCOUNT
                                 * **/

                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  offers.push(item);
                                }

                                if (item.is_first_order === false) {
                                  offers.push(item);
                                }
                              }
                            }
                          } // end time compare
                        } else {
                          /**
                           * - NOT TIME GIVEN
                           * **/
                          if (item.is_default) {
                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              offers.push(item);
                            }

                            if (item.is_first_order === false) {
                              offers.push(item);
                            }
                            // default discount added directly
                            if (!sessionStorage.getItem('promoId')) {
                              this.store.saveAcceptedPromotion(item);
                            }
                          } else {
                            /**
                             * - NOT DEFAULT * - USER HAVE TO ACCEPT DISCOUNT
                             * **/

                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              offers.push(item);
                            }

                            if (item.is_first_order === false) {
                              offers.push(item);
                            }
                          }
                        }
                      }
                    } else {
                      /*
                        * - IF START AND END DATE NOT GIVEN.
                        * */
                      if (item.offer_available_time.length > 0) {
                        const dayName = currentTime.getDay();
                        const _times = item.offer_available_time.filter((book: any) => book.day_name === dayName);
                        if (_times.length > 0) {
                          const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                          const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                          // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                          if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                            if (item.is_default) {
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                offers.push(item);
                              }

                              if (item.is_first_order === false) {
                                offers.push(item);
                              }
                              // default discount added directly
                              if (!sessionStorage.getItem('promoId')) {
                                this.store.saveAcceptedPromotion(item);
                              }
                            } else {
                              /**
                               * - NOT DEFAULT
                               * - USER HAVE TO ACCEPT DISCOUNT
                               * **/

                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                offers.push(item);
                              }

                              if (item.is_first_order === false) {
                                offers.push(item);
                              }
                            }
                          }
                        } // end time compare
                      } else {
                        if (item.is_default) {
                          /**
                           * - COUNT USER PREVIOUS ORDERS
                           * **/
                          if (item.is_first_order && (user && user.total_orders === 0)) {
                            offers.push(item);
                          }

                          if (item.is_first_order === false) {
                            offers.push(item);
                          }
                          // default discount added directly
                          if (!sessionStorage.getItem('promoId')) {
                            this.store.saveAcceptedPromotion(item);
                          }
                        } else {
                          /**
                           * - NOT DEFAULT
                           * - USER HAVE TO ACCEPT DISCOUNT
                           * **/

                          if (item.is_first_order && (user && user.total_orders === 0)) {
                            offers.push(item);
                          }

                          if (item.is_first_order === false) {
                            offers.push(item);
                          }
                        }
                      }
                    }
                  }
                } else {
                  offers = [];
                  this.store.saveAcceptedPromotion({});
                }
              });
            }
          }

          if (discountsData.length > 0) {
            /**
             * COLLECTION AND DELIVERY BOTH= 0
             * COLLECTION = 1
             * DELIVER = 2
             * **/
            const dis = discountsData.filter((item: any) =>
              item?.its_type === 1
              && totalAmount >= item.discount_eligible_amount
              && item.is_active === true
              && this.clientPlatformService.isPromotionAvailable(item.platform)
            );
            if (dis.length > 0) {

              dis.forEach((item: any) => {
                if (Number(item.discount_eligible_for) === selected_policy || Number(item.discount_eligible_for) === 0) {
                  /**
                   * - CHECK ELIGIBLE PAYMENT METHODS CASH = 1, CARD = 2, BOTH = 0
                   * - CHECK DISCOUNT START DATE AND END DATE IF NO DATE FIXED THEN IT'S FOR ALL DAY
                   * - CHECK DISCOUNT AVAILABLE TIMES
                   * - CHECK DEFAULT (AUTOMATIC ACCEPT DISCOUNT)
                   * - CHECK IS IT FIRST ORDER BY CUSTOMER
                   * **/

                  if (selected_payment) {
                    if (item.eligible_payment_methods === selected_payment || item.eligible_payment_methods === 0) {
                      /*
                        * - EITHER CASH OR CARD * CASH = 1; CARD = 2
                        * */

                      if (item.start_date !== null && item.end_date !== null) {
                        /**
                         * - IF START AND END DATE ASSIGNED
                         */
                        const start_date = new Date(item.start_date);
                        const end_date = new Date(item.end_date);
                        if (currentTime >= start_date && currentTime <= end_date) {

                          if (item.discount_available_time.length > 0) {

                            const dayName = currentTime.getDay();
                            const _times = item.discount_available_time.filter((book: any) => book.day_name === dayName);
                            if (_times.length > 0) {
                              const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                              const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                              
                              // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                              if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                                if (item.is_default) {

                                  /*** - COUNT USER PREVIOUS ORDERS * **/
                                  if (item.is_first_order && (user && user.total_orders === 0)) {
                                    discount.push(item);
                                  }
                                  if (item.is_first_order === false) {
                                    discount.push(item);
                                  }
                                  // default discount added directly
                                  if (!sessionStorage.getItem('promoId')) {
                                    this.store.saveAcceptedPromotion(item);
                                  }
                                } else {
                                  /**
                                   * - NOT DEFAULT
                                   * - USER HAVE TO ACCEPT DISCOUNT
                                   * **/

                                  /*** - COUNT USER PREVIOUS ORDERS * **/
                                  if (item.is_first_order && (user && user.total_orders === 0)) {
                                    discount.push(item);
                                  }
                                  if (item.is_first_order === false) {
                                    discount.push(item);
                                  }
                                }
                              } // end time compare
                            }
                          } else {
                            /**
                             * - NOT TIME GIVEN
                             * **/
                            if (item.is_default) {
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                discount.push(item);
                              }
                              if (item.is_first_order === false) {
                                discount.push(item);
                              }
                              // default discount added directly
                              if (!sessionStorage.getItem('promoId')) {
                                this.store.saveAcceptedPromotion(item);
                              }
                            } else {
                              /**
                               * - NOT DEFAULT * - USER HAVE TO ACCEPT DISCOUNT
                               * **/


                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                discount.push(item);
                              }
                              if (item.is_first_order === false) {
                                discount.push(item);
                              }
                            }
                          }
                        }
                      } else {
                        /*
                          * - IF START AND END DATE NOT GIVEN (if it is NULL).
                          * - PROCESS WITH TIME ELSE DEFAULT
                          * */

                        if (item.discount_available_time.length > 0) {
                          const dayName = currentTime.getDay();
                          const _times = item.discount_available_time.filter((book: any) => book.day_name === dayName);
                          if (_times.length > 0) {
                            const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                            // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                            if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                              if (item.is_default) {
                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  discount.push(item);
                                }
                                if (item.is_first_order === false) {
                                  discount.push(item);
                                }
                                // default discount added directly
                                if (!sessionStorage.getItem('promoId')) {
                                  this.store.saveAcceptedPromotion(item);
                                }

                              } else {
                                /**
                                 * - NOT DEFAULT
                                 * - USER HAVE TO ACCEPT DISCOUNT
                                 * **/

                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  discount.push(item);
                                }
                                if (item.is_first_order === false) {
                                  discount.push(item);
                                }
                              }
                            }
                          } // end time compare
                        } else {
                          if (item.is_default) {
                            /**
                             * - COUNT USER PREVIOUS ORDERS
                             * **/
                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              discount.push(item);
                            }
                            if (item.is_first_order === false) {
                              discount.push(item);
                            }
                            // default discount added directly
                            if (!sessionStorage.getItem('promoId')) {
                              this.store.saveAcceptedPromotion(item);
                            }
                          } else {
                            /**
                             * - NOT DEFAULT
                             * - USER HAVE TO ACCEPT DISCOUNT
                             * **/

                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              discount.push(item);
                            }
                            if (item.is_first_order === false) {
                              discount.push(item);
                            }
                          }
                        }
                      }
                    }
                  } else {
                    /**
                     * - IF USER DID NOT SELECT PAYMENT METHOD YET!
                     * **/

                    /*
                      * - EITHER CASH OR CARD * CASH = 1; CARD = 2
                      * */

                     if (item.start_date !== null && item.end_date !== null) {
                      /**
                       * - IF START AND END DATE ASSIGNED
                       */


                      const start_date = new Date(item.start_date);
                      const end_date = new Date(item.end_date);
                      if (currentTime >= start_date && currentTime <= end_date) {

                        if (item.discount_available_time.length > 0) {
                          const dayName = currentTime.getDay();
                          const _times = item.discount_available_time.filter((book: any) => book.day_name === dayName);
                          if (_times.length > 0) {

                            const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                            // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                            if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                              if (item.is_default) {
                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && !user) {
                                  discount.push(item);
                                  // default discount added directly
                                  if (!sessionStorage.getItem('promoId')) {
                                    this.store.saveAcceptedPromotion(item);
                                  }
                                  return
                                }

                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  discount.push(item);
                                  // default discount added directly
                                  if (!sessionStorage.getItem('promoId')) {
                                    this.store.saveAcceptedPromotion(item);
                                  }
                                  return
                                }
                                if (item.is_first_order === false) {
                                  discount.push(item);
                                  // default discount added directly
                                  if (!sessionStorage.getItem('promoId')) {
                                    this.store.saveAcceptedPromotion(item);
                                  }
                                  return
                                }
                              } else {
                                /**
                                 * - NOT DEFAULT
                                 * - USER HAVE TO ACCEPT DISCOUNT
                                 * **/

                                /*** - COUNT USER PREVIOUS ORDERS * **/
                                if (item.is_first_order && (user && user.total_orders === 0)) {
                                  discount.push(item);
                                }
                                if (item.is_first_order === false) {

                                  discount.push(item);
                                }
                              }
                            }
                          } // end time compare
                        } else {
                          /**
                           * - NOT TIME GIVEN
                           * **/
                          if (item.is_default) {
                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              discount.push(item);
                            }
                            if (item.is_first_order === false) {
                              discount.push(item);
                            }
                            // default discount added directly
                            if (!sessionStorage.getItem('promoId')) {
                              this.store.saveAcceptedPromotion(item);
                            }
                          } else {
                            /**
                             * - NOT DEFAULT * - USER HAVE TO ACCEPT DISCOUNT
                             * **/

                            /*** - COUNT USER PREVIOUS ORDERS * **/
                            if (item.is_first_order && (user && user.total_orders === 0)) {
                              discount.push(item);
                            }

                            if (item.is_first_order === false) {
                              discount.push(item);
                            }
                          }
                        }
                      }
                    } else {
                      /*
                        * - IF START AND END DATE NOT GIVEN.
                        * */
                       if (item.discount_available_time.length > 0) {
                        
                         const dayName = currentTime.getDay();
                         const _times = item.discount_available_time.filter((book: any) => book.day_name === dayName);
                         if (_times.length > 0) {
                           const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].start_time));
                           const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + _times[0].end_time));
                           // if (currentTime >= start_time && currentTime <= end_time || currentTime < start_time) {
                            if ((currentTime >= start_time && currentTime <= end_time) || ((currentTime >= start_time && convertedWhenTime >= start_time) && (currentTime <= end_time && convertedWhenTime <= end_time))) {
                            if (item.is_default) {
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                discount.push(item);
                              }
                              if (item.is_first_order === false) {
                                discount.push(item);
                              }
                              // default discount added directly
                              if (!sessionStorage.getItem('promoId')) {
                                this.store.saveAcceptedPromotion(item);
                              }
                            } else {
                              /**
                               * - NOT DEFAULT
                               * - USER HAVE TO ACCEPT DISCOUNT
                               * **/

                              /*** - COUNT USER PREVIOUS ORDERS * **/
                              if (item.is_first_order && (user && user.total_orders === 0)) {
                                discount.push(item);
                              }
                              if (item.is_first_order === false) {
                                discount.push(item);
                              }
                            }
                          }
                        } // end time compare
                      } else {
                        
                        if (item.is_default) {
                          /**
                           * - COUNT USER PREVIOUS ORDERS
                           * **/
                          /*** - COUNT USER PREVIOUS ORDERS * **/
                          if (item.is_first_order && (user && user.total_orders === 0)) {
                            discount.push(item);
                          }
                          if (item.is_first_order === false) {
                            discount.push(item);
                          }
                          // default discount added directly
                          if (!sessionStorage.getItem('promoId')) {
                            this.store.saveAcceptedPromotion(item);
                          }
                        } else {
                          /**
                           * - NOT DEFAULT
                           * - USER HAVE TO ACCEPT DISCOUNT
                           * **/

                          /*** - COUNT USER PREVIOUS ORDERS * **/
                          if (item.is_first_order && (user && user.total_orders === 0)) {
                            discount.push(item);
                          }
                          if (item.is_first_order === false) {
                            discount.push(item);
                          }
                        }
                      }
                    }
                  }
                }
              });
            }
          }
          const availableOffersDiscount = discount.concat(offers);
          resolve(availableOffersDiscount);
        }, 200);
      }
    });
  }

  private parseTimeToMinutes(timeStr: any): number | null {
    if (!timeStr) {
      return null;
    }

    const trimmed = String(timeStr).trim();
    if (!trimmed) {
      return null;
    }

    const parts = trimmed.split(/\s+/);
    const timePart = parts[0];
    const [hoursStr, minutesStr] = timePart.split(':');

    let hours = Number(hoursStr);
    const minutes = Number(minutesStr);

    if (parts.length > 1) {
      const modifier = parts[1].toUpperCase();
      if (modifier === 'PM' && hours !== 12) {
        hours += 12;
      }
      if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }
    }

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    return (hours * 60) + minutes;
  }

  convertToDate(timeStr: any, baseTimeStr?: any, baseDate: Date = new Date()) {
    const timeMinutes = this.parseTimeToMinutes(timeStr);
    if (timeMinutes === null) {
      return new Date(NaN);
    }

    const date = new Date(baseDate);
    const hours = Math.floor(timeMinutes / 60);
    const minutes = timeMinutes % 60;
    date.setHours(hours, minutes, 0, 0);

    if (baseTimeStr) {
      const baseMinutes = this.parseTimeToMinutes(baseTimeStr);
      if (baseMinutes !== null && timeMinutes < baseMinutes) {
        date.setDate(date.getDate() + 1);
      }
    }

    return date;
  }

  getASAPTime(deliveryTime: any) {
    const currentTime = new Date();
    return this.changeTimeFormat(new Date(currentTime.getTime() + Number(deliveryTime) * 60000));
  }

  stringTitleCase(str: any) {
    return str.toLowerCase().split(' ').map(function (word: any) {
      return (word.charAt(0).toUpperCase() + word.slice(1));
    }).join(' ');
  }

  goToWebLink(data: any) {
    if (isPlatformBrowser(this.platformId)) {
      var url = String(data).replace(/^(https?:\/\/)?(www\.)?/, 'http://');
      window.open(url, '_blank',);
    }
  }

  generateCartHashKey(dishData) {
    let dish_extra_id = '';
    if (dishData['dish_extra']) {
      dishData['dish_extra'].forEach(grp => {
        dish_extra_id = dish_extra_id.concat(grp['_id']);
        grp['option'].forEach(opt => {
          dish_extra_id = dish_extra_id.concat(opt['_id']);
        });
      });
    }
    const checksum_data = {
      _id: dishData['_id'],
      dish_id: dishData['dish_id'],
      dish_instruction: dishData['dish_instruction'] ? dishData['dish_instruction']?.trim()?.toLowerCase() : '',
      dish_extra_id: dish_extra_id
    }
    const md5 = new Md5();
    let checksum = md5.appendStr(JSON.stringify(checksum_data)).end();
    return checksum;
  }

  generateCartDataHashKey(cartData: any, _for?: string) {
    const md5 = new Md5();
    let checksum = md5.appendStr(JSON.stringify(cartData)).end();
    if (this.platformId === 'browser') {
      if (_for && _for === 'reservation-menu') {
        sessionStorage.setItem('generated_res_cart_data', JSON.stringify(checksum));
      } else {
        sessionStorage.setItem('generated_cart_data', JSON.stringify(checksum));
      }
    }
  }

  getAvailablePromotions(subTotalExcludeOffer: any, discountsData: any, offersData: any) {
    return new Promise((resolve) => {
      let eligiblePromotionList = [];
      this.checkPromotionEligibility(subTotalExcludeOffer, discountsData, offersData).then((result: any) => {
        result.forEach((item: any) => {
          eligiblePromotionList.push(item);
        });
        const currentTime = new Date();

        const filteredList = eligiblePromotionList.filter(item => {
          const start_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + item.start_time));
          const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + item.end_time));
          return ((currentTime >= start_time && currentTime <= end_time) && item.is_active);
        })
        this.store.saveAvailablePromotion(filteredList);
        resolve('check & save');
      });
    });
  }

  getAcceptedDiscount(subTotalExcludeOffer: any, AcceptedData: any) {
    return new Promise((resolve) => {
      if (!AcceptedData || !this.clientPlatformService.isPromotionAvailable(AcceptedData.platform)) {
        this.store.saveAcceptedPromotion({});
        resolve(false);
        return;
      }

      /** its_type=1=discount, its_type=2=offer **/
      let selected_policy: any;
      let selected_payment: any;
      this.store.selected_policy.subscribe((result: any) => {
        if (result && result['policy']) {
          selected_policy = result['policy'] === 'COLLECTION' ? 1 : 2;
        } else {
          selected_policy = 2;
        }
      });
      this.store.paymentMethod.subscribe((result: any) => {
        if (result && result['is_active']) {
          selected_payment = result['paymentType'] === 'CARD' ? 2 : 1
        }
      });
      switch (AcceptedData?.its_type) {
        case 1:
          if(AcceptedData){
            const currentTime = new Date()
            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + AcceptedData.end_time));
            if ((currentTime >= end_time)){
              this.store.saveAcceptedPromotion({});
              return
            }
          }
          if (
            (selected_policy === Number(AcceptedData.discount_eligible_for) || Number(AcceptedData.discount_eligible_for) === 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods === selected_payment || AcceptedData.eligible_payment_methods === 0)
            && subTotalExcludeOffer >= AcceptedData.discount_eligible_amount
          ) {
            /**
             * SET ACCEPTED DISCOUNT DATA
             * Eligible policy either collection/delivery or both
             * col=1, del =2, both=0,
             * Eligible payment method selected
             * cash=1, card 2, both 0
            **/
           resolve(AcceptedData);
          } else if (
            (selected_policy === Number(AcceptedData.discount_eligible_for) || Number(AcceptedData.discount_eligible_for) === 0)
            && !selected_payment
            && subTotalExcludeOffer >= AcceptedData.discount_eligible_amount
          ) {
            /**-----SET ACCEPTED DISCOUNT DATA UNTIL USER SELECT PAYMENT METHOD-----**/
            resolve(AcceptedData);
          } else if (
            (selected_policy === Number(AcceptedData.discount_eligible_for) || Number(AcceptedData.discount_eligible_for) === 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods !== selected_payment || AcceptedData.eligible_payment_methods !== 0)
            && subTotalExcludeOffer < AcceptedData.discount_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED PAYMENT METHOD NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            (selected_policy !== Number(AcceptedData.discount_eligible_for) || Number(AcceptedData.discount_eligible_for) !== 0)
            && !selected_payment
            || subTotalExcludeOffer < AcceptedData.discount_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED policy NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            (selected_policy !== Number(AcceptedData.discount_eligible_for) || Number(AcceptedData.discount_eligible_for) !== 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods !== selected_payment || AcceptedData.eligible_payment_methods !== 0)
            || subTotalExcludeOffer < AcceptedData.discount_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED PAYMENT METHOD NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            subTotalExcludeOffer < AcceptedData.discount_eligible_amount
          ) {
            this.store.saveAcceptedPromotion({});
          }
          break;

        case 2:
          if(AcceptedData){
            const currentTime = new Date()
            const end_time = new Date(Date.parse(currentTime.getFullYear() + '/' + (currentTime.getMonth() + 1) + '/' + currentTime.getDate() + ' ' + AcceptedData.end_time));
            if ((currentTime >= end_time)){
              this.store.saveAcceptedPromotion({});
              return
            }
          }
          
          if (
            (selected_policy === Number(AcceptedData.offer_eligible_for) || Number(AcceptedData.offer_eligible_for) === 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods === selected_payment || AcceptedData.eligible_payment_methods === 0)
            && subTotalExcludeOffer >= AcceptedData.offer_eligible_amount
          ) {
            /**
             * SET ACCEPTED DISCOUNT DATA
             * Eligible policy either collection/delivery or both
             * col=1, del =2, both=0,
             * Eligible payment method selected
             * cash=1, card 2, both 0
            **/
            resolve(AcceptedData);
          } else if (
            (selected_policy === Number(AcceptedData.offer_eligible_for) || Number(AcceptedData.offer_eligible_for) === 0)
            && !selected_payment
            && subTotalExcludeOffer >= AcceptedData.offer_eligible_amount
          ) {
            /**SET ACCEPTED DISCOUNT DATA UNTIL USER SELECT PAYMENT METHOD**/
            resolve(AcceptedData);
          } else if (
            (selected_policy === Number(AcceptedData.offer_eligible_for) || Number(AcceptedData.offer_eligible_for) === 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods !== selected_payment || AcceptedData.eligible_payment_methods !== 0)
            || subTotalExcludeOffer < AcceptedData.offer_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED PAYMENT METHOD NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            (selected_policy !== Number(AcceptedData.offer_eligible_for) || Number(AcceptedData.offer_eligible_for) !== 0)
            && !selected_payment
            || subTotalExcludeOffer < AcceptedData.offer_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED policy NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            (selected_policy !== Number(AcceptedData.offer_eligible_for) || Number(AcceptedData.offer_eligible_for) !== 0)
            && selected_payment
            && (AcceptedData.eligible_payment_methods !== selected_payment || AcceptedData.eligible_payment_methods !== 0)
            || subTotalExcludeOffer < AcceptedData.offer_eligible_amount
          ) {
            /**
             * RESET ACCEPTED DISCOUNT DATA
             * SELECTED PAYMENT METHOD NOT ELIBIBLE
            **/
            this.store.saveAcceptedPromotion({});
          } else if (
            subTotalExcludeOffer < AcceptedData.offer_eligible_amount
          ) {
            this.store.saveAcceptedPromotion({});
          }
          break;

      }
    });
  }

  public calculateDishVat(dishData: any, discountData: any, totalDish: number) {
    if (!totalDish || totalDish <= 0) return;
    // check vat is enable or not
    if (!this.restaurantData || !this.restaurantData['is_vat_enable']) return;
    // check has discout or not
    // discount amount deduction from dish vat
    let deductedAmount: number = 0;
    if (discountData['amount'] && discountData['amount'] > 0) {
      if (discountData['type'] === 1) {// dish vat deduction by percentage
        deductedAmount = Number(((dishData['unit_price'] * discountData['rate']) / 100).toFixed(4));
      } else {// dish vat deduction by fixed
        deductedAmount = discountData['amount'] / totalDish;
      }
    }
    // declare the property
    let price: number = Number((((dishData['unit_price'] - deductedAmount)) - dishData['package_price']) * dishData['quantity']);
    let vatType: string = this.restaurantData.is_vat_included ? 'include' : 'exclude';
    let vatRate: number = dishData['vat_rate'] !== null ? dishData['vat_rate'] : this.findUpperLabelVatRate(dishData['category_id']);

    let includeFormula = Number(((price / ((100 + vatRate) / 100)) * (vatRate / 100)).toFixed(4));
    let excludeFormula = Number(((price * vatRate) / 100).toFixed(4));
    let vatableAmount: number = vatType === 'include' ? Number(includeFormula.toFixed(4)) : Number(excludeFormula.toFixed(4));
    return { vat_amount: vatableAmount, vat_type: vatType, vat_rate: vatRate };
  }

  findUpperLabelVatRate(categoryId: string): number {
    let vat_rate: number = 0;
    const dishCategory = this.restaurantDish?.flatMap(cuisine => cuisine?.dish_categories).find(category => category._id === categoryId);
    if (dishCategory && dishCategory.vat_rate) {
      return vat_rate = dishCategory.vat_rate;
    } else if (this.restaurantData.vat_rate) {
      return vat_rate = this.restaurantData.vat_rate;
    }
    return vat_rate;
  }

  public roundUp(num: number) {
    const roundedNum = Math.round(num * 100) / 100; // round to two decimal places
    const decimalPart = roundedNum - Math.trunc(roundedNum); // get the decimal part
    if (decimalPart >= 0.005) {
      return Math.ceil(roundedNum * 100) / 100; // round up if third decimal place is 5 or greater than 5
    } else {
      return roundedNum;
    }
  }

  showSuccessToast(msg: any) {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'success',
      title: `${msg} successfully`,
      showConfirmButton: false,
      timer: 1500,
      customClass: {
        popup: 'custom-toast-background',
        icon: 'custom-toast-icon',
        title: 'custom-toast-title'
      },
      onBeforeOpen: (toast) => {
        // Ensure all animations and transitions are disabled
        toast.style.animation = 'none';
        toast.style.transition = 'none';
        toast.style.opacity = '1';

        // Add custom animation to the icon
        const icon = toast.querySelector('.swal2-icon.swal2-success');
        if (icon) {
          icon.classList.add('custom-icon-animation');
        }
      }
    });
  }
}
